import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Tag, Button, Spin, Typography, Alert, Divider, Steps, Timeline } from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  TruckOutlined,
  HomeOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import { useGlobalStore, type GlobalState } from '@digiwedge/global-state';
import { fetchSalesOrders, type SalesOrder } from '@digiwedge/axiz_api';

const { Title, Text } = Typography;

const getStatusColor = (status: string): string => {
  const statusColors: Record<string, string> = {
    New: 'blue',
    Processing: 'orange',
    Shipped: 'cyan',
    Delivered: 'green',
    Cancelled: 'red',
    Refunded: 'purple',
  };
  return statusColors[status] || 'default';
};

const getStatusStep = (status: string): number => {
  const steps: Record<string, number> = {
    New: 0,
    Processing: 1,
    Shipped: 2,
    Delivered: 3,
  };
  return steps[status] ?? 0;
};

const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatAmount = (amount: number): string => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
  }).format(amount);
};

export function UserOrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const [order, setOrder] = useState<SalesOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const isAuthenticated = useGlobalStore((s: GlobalState) => s.isAuthenticated);
  const profile = useGlobalStore((s: GlobalState) => s.profile);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/orders/${orderId}` } });
      return;
    }

    if (!orderId || !Number.isFinite(Number(orderId))) {
      setError('Order ID is invalid');
      setLoading(false);
      return;
    }

    const loadOrder = async () => {
      try {
        setLoading(true);
        setError(null);
        if (!profile?.email) {
          setError('Profile email is missing. Please sign out and back in.');
          return;
        }

        const targetId = String(orderId);
        const pageSize = 50;
        let page = 1;
        let found: SalesOrder | null = null;

        while (!found && page <= 5) {
          const pageOrders = await fetchSalesOrders({
            customerEmail: profile.email,
            pageSize,
            page,
          });
          found = pageOrders.find((item) => String(item.id) === targetId) ?? null;
          if (found || pageOrders.length < pageSize) break;
          page += 1;
        }

        if (!found) {
          setError('Order not found for this account');
          return;
        }
        setOrder(found);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load order');
      } finally {
        setLoading(false);
      }
    };

    loadOrder();
  }, [isAuthenticated, navigate, orderId, profile?.email]);

  if (!isAuthenticated) {
    return null; // Will redirect
  }

  if (loading) {
    return (
      <div style={{ maxWidth: 800, margin: '48px auto', textAlign: 'center' }}>
        <Spin size="large" />
        <Text style={{ display: 'block', marginTop: 16 }}>Loading order details...</Text>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div style={{ maxWidth: 800, margin: '48px auto', padding: '0 16px' }}>
        <Alert
          type="error"
          message="Order Not Found"
          description={error || 'The order you are looking for could not be found.'}
          showIcon
          action={
            <Link to="/orders">
              <Button>Back to Orders</Button>
            </Link>
          }
        />
      </div>
    );
  }

  const isCancelledOrRefunded = order.status === 'Cancelled' || order.status === 'Refunded';

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ marginBottom: 24 }}>
        <Link to="/orders">
          <Button type="link" icon={<ArrowLeftOutlined />} style={{ padding: 0 }}>
            Back to My Orders
          </Button>
        </Link>
      </div>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              Order #{order.reference}
            </Title>
            <Text type="secondary">Placed on {formatDate(order.orderDate)}</Text>
          </div>
          <Tag color={getStatusColor(order.status)} style={{ fontSize: 14, padding: '4px 12px' }}>
            {order.status}
          </Tag>
        </div>

        {!isCancelledOrRefunded && (
          <>
            <Steps
              current={getStatusStep(order.status)}
              items={[
                { title: 'Order Placed', icon: <ShoppingOutlined /> },
                { title: 'Processing', icon: <ClockCircleOutlined /> },
                { title: 'Shipped', icon: <TruckOutlined /> },
                { title: 'Delivered', icon: <HomeOutlined /> },
              ]}
              style={{ marginBottom: 32 }}
            />
            <Divider />
          </>
        )}

        <Descriptions
          title="Order Details"
          bordered
          column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
        >
          <Descriptions.Item label="Order Reference">{order.reference}</Descriptions.Item>
          <Descriptions.Item label="Order Date">{formatDate(order.orderDate)}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={getStatusColor(order.status)}>{order.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Total Amount">
            <Text strong style={{ fontSize: 16, color: '#1890ff' }}>
              {formatAmount(order.totalAmount)}
            </Text>
          </Descriptions.Item>
        </Descriptions>

        <Divider />

        <Title level={5}>Order Timeline</Title>
        <Timeline
          items={[
            {
              dot: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
              children: (
                <div>
                  <Text strong>Order Placed</Text>
                  <br />
                  <Text type="secondary">{formatDate(order.orderDate)}</Text>
                </div>
              ),
            },
            ...(order.status !== 'New'
              ? [
                  {
                    dot: <ClockCircleOutlined style={{ color: '#1890ff' }} />,
                    children: (
                      <div>
                        <Text strong>Processing Started</Text>
                        <br />
                        <Text type="secondary">Your order is being prepared</Text>
                      </div>
                    ),
                  },
                ]
              : []),
            ...(order.status === 'Shipped' || order.status === 'Delivered'
              ? [
                  {
                    dot: <TruckOutlined style={{ color: '#13c2c2' }} />,
                    children: (
                      <div>
                        <Text strong>Shipped</Text>
                        <br />
                        <Text type="secondary">Your order is on its way</Text>
                      </div>
                    ),
                  },
                ]
              : []),
            ...(order.status === 'Delivered'
              ? [
                  {
                    dot: <HomeOutlined style={{ color: '#52c41a' }} />,
                    children: (
                      <div>
                        <Text strong>Delivered</Text>
                        <br />
                        <Text type="secondary">Your order has been delivered</Text>
                      </div>
                    ),
                  },
                ]
              : []),
          ]}
        />

        <Divider />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Link to="/orders">
            <Button icon={<ArrowLeftOutlined />}>Back to Orders</Button>
          </Link>
          <Link to="/">
            <Button type="primary">Continue Shopping</Button>
          </Link>
        </div>
      </Card>
    </div>
  );
}

export default UserOrderDetailPage;
